import { autoinject, computedFrom } from 'aurelia-framework';
import { ValidationRules } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { WidgetController } from 'controls/as-widget-container';
import { AirlineStatus, TransportationSystemType } from 'model/business-enums';
import { Airline } from 'model/business-keywords';
import { TransportSegment } from 'model/forms/trip-details';
import { KeywordsService } from 'services/keywords-service';
import { LookupsService } from 'services/lookups-service';
import { WidgetBase } from 'support/base_classes/widget-base';
import { MAX_DATE } from 'support/settings';

@autoinject
export class TransportSegmentWidget extends WidgetBase<TransportSegment, WidgetController> {
  public today = new Date().toISOString().slice(0, 10);
  public maxDate = MAX_DATE.toISOString().slice(0, 10);

  constructor(private keywords: KeywordsService, private lookups: LookupsService) {
    super();
  }

  bind() {
    ValidationRules
      .ensureObject<TransportSegment>().required()
      .ensure(model => model.type).displayName('Transport type').required()
      .ensure(model => model.departureDate).displayName('Departure date').required().satisfiesRule('minDate').satisfiesRule('maxDate')
      .ensure(model => model.airlineName).displayName('Airline').required()
      .ensure(model => model.flightNumber).displayName('Flight number').required().matches(/^([A-Za-z]{2}|[A-Za-z]\d|\d[A-Za-z])[0-9](\d{1,3})?$/).withMessage('Flight number is not valid.')
      .ensure(model => model.note).displayName('Transport info').required()
      .on(this.model);
  }

  @computedFrom('model.type.formIcon')
  get widgetIcon() {
    return this.model.type?.formIcon || 'fas fa-bicycle';
  }

  @computedFrom('model.type.dataType')
  get showFlight() {
    return this.model.type?.dataTypeValue === TransportationSystemType.Plane;
  }

  @computedFrom('keywords.transportTypes', 'model.type')
  get transportTypes() {
    return this.keywords.ensureItem(this.keywords.transportTypes, this.model.type);
  }

  @computedFrom('model.airline', 'model.airlineName')
  get airlineLabel() {
    let airlineStatusLabel = null;
    if (this.model.airline !== null) {
      switch (this.model.airline.status.value) {
        case AirlineStatus.ApprovedDeviation:
          airlineStatusLabel = 'Approved by Deviation';
          break;
        case AirlineStatus.NonApproved:
          airlineStatusLabel = 'not Approved';
          break;
        default:
          break;
      }
    }
    else if (this.model.airlineName !== null)
      airlineStatusLabel = 'not Approved';
    return `Airline${airlineStatusLabel !== null ? (` (selected Airline is ${airlineStatusLabel})`) : ''}`;
  }

  keywordsItemMatcher = this.keywords.itemsMatch;
  airlinesLookupOptions = this.lookups.getAirlinesLookupOptions();

  @watch((x: TransportSegmentWidget) => x.model.airline)
  private airlineChanged(newValue: Airline, oldValue: Airline) {
    if (this.model.flightNumber == null || this.model.flightNumber === oldValue?.code)
      this.model.flightNumber = newValue?.code ?? null;
  }
}
